/**
 * Web Component wrapper for Button
 * Custom element: <hf-button>
 *
 * @generated by scripts/generate-wc-components.ts
 * DO NOT EDIT MANUALLY
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Button } from '../components/atoms/buttons/Button';
import { parseAttr, parseValue, type PropDefs } from './_shared';

/**
 * Prop type definitions for attribute parsing
 */
const propTypes: PropDefs = {
    size: 'string',
    "aria-label": 'string',
    "aria-labelledby": 'string',
    "aria-describedby": 'string',
    "aria-controls": 'string',
    "aria-expanded": 'boolean',
    "aria-invalid": 'boolean',
    "aria-required": 'boolean',
    role: 'string',
    title: 'string',
    disabled: 'boolean',
    name: 'string',
    type: 'string',
    value: 'string',
    appearance: 'string',
    variant: 'string',
    shape: 'string',
    loading: 'boolean',
    nativeButton: 'boolean',
    startIcon: 'json',
    endIcon: 'json',
    dotIndicator: 'boolean',
    dotColor: 'string',
    badge: 'json',
    iconOnly: 'boolean',
    animated: 'boolean',
    render: 'json'
};

class ButtonWC extends HTMLElement {
  private _root: ReturnType<typeof createRoot> | null = null;
  private _childrenContainer: HTMLDivElement | null = null;
  private _props: Record<string, unknown> = {};
  private _initialized = false;

  connectedCallback() {
    setTimeout(() => this._initialize(), 0);
  }

  private _initialize() {
    if (this._initialized) return;
    this._initialized = true;

    const originalChildren: Node[] = [];
    for (const child of Array.from(this.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE && !child.textContent?.trim()) {
        continue;
      }
      originalChildren.push(child);
    }

    const reactContainer = this.ownerDocument.createElement('div');
    reactContainer.style.display = 'contents';

    this._childrenContainer = this.ownerDocument.createElement('div');
    this._childrenContainer.style.display = 'contents';

    originalChildren.forEach((child) => this._childrenContainer!.appendChild(child));

    while (this.firstChild) this.removeChild(this.firstChild);
    this.appendChild(reactContainer);

    this._root = createRoot(reactContainer);
    this._render();
  }

  disconnectedCallback() {
    this._root?.unmount();
    this._root = null;
  }

  attributeChangedCallback(_name: string, _oldValue: string | null, _newValue: string | null) {
    this._render();
  }

  static get observedAttributes() {
    return Object.keys(propTypes).map((prop) =>
      prop.replace(/([A-Z])/g, '-$1').toLowerCase(),
    );
  }

  _setProp(name: string, value: unknown) {
    const propType = propTypes[name] || 'string';
    this._props[name] = parseValue(value, propType);
    this._render();
  }

  _getProp(name: string): unknown {
    if (name in this._props) {
      return this._props[name];
    }
    const attrName = name.replace(/([A-Z])/g, '-$1').toLowerCase();
    const propType = propTypes[name] || 'string';
    return parseAttr(this.getAttribute(attrName), propType);
  }

  private _render() {
    if (!this._root || !this._childrenContainer) return;

    const props: Record<string, unknown> = {};
    for (const [propName, propType] of Object.entries(propTypes)) {
      if (propName in this._props) {
        props[propName] = this._props[propName];
      } else {
        const attrName = propName.replace(/([A-Z])/g, '-$1').toLowerCase();
        props[propName] = parseAttr(this.getAttribute(attrName), propType);
      }
    }

    const element = React.createElement(
      Button,
      props,
      React.createElement('span', {
        ref: (el: HTMLSpanElement | null) => {
          if (el && this._childrenContainer) {
            el.replaceWith(this._childrenContainer);
          }
        },
        style: { display: 'contents' },
      }),
    );

    this._root.render(element);
  }
}

for (const propName of Object.keys(propTypes)) {
  Object.defineProperty(ButtonWC.prototype, propName, {
    get() {
      return this._getProp(propName);
    },
    set(value: unknown) {
      this._setProp(propName, value);
    },
    enumerable: true,
    configurable: true,
  });
}

if (typeof customElements !== 'undefined' && !customElements.get('hf-button')) {
  customElements.define('hf-button', ButtonWC);
}

export { ButtonWC };
export default ButtonWC;
